import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { fetchAllTools } from '../services/toolsApi';
import toolsSnapshot from '../data/tools-snapshot.json';
import toolLandingPages, { getToolName } from '../data/toolLandingPages';
import CallbackModal from '../components/CallbackModal';
import Header from '../components/Header';
import CallbackCTA from '../components/rent/CallbackCTA';
import ToolBottomSheet from '../components/rent/ToolBottomSheet';
import ToolCard from '../components/rent/ToolCard';
import StickyCtaBar from '../components/landing/StickyCtaBar';
import ReviewPrompt from '../components/landing/ReviewPrompt';
import { usePageMeta } from '../utils/usePageMeta';
import { absoluteUrl } from '../utils/langPath';
import { buildLocalBusiness, jsonLd } from '../utils/schema';

const faqKeys = ['faq1', 'faq2', 'faq3', 'faq5', 'faq6'];

export default function RentPage() {
  const { t, language, lp } = useLanguage();
  const initialTools = Array.isArray(toolsSnapshot) ? toolsSnapshot : [];
  const [tools, setTools] = useState(initialTools);
  const [loading, setLoading] = useState(initialTools.length === 0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [showCallback, setShowCallback] = useState(false);
  const [callbackToolName, setCallbackToolName] = useState(null);
  const [callbackRecommended, setCallbackRecommended] = useState([]);

  const visibleTools = tools.filter((tool) => tool.id !== 'tool-test');

  const openCallback = (toolName = null, recommended = []) => {
    setCallbackToolName(toolName);
    setCallbackRecommended(recommended);
    setShowCallback(true);
  };

  usePageMeta({
    title: t('rentPage.metaTitle'),
    description: t('rentPage.metaDescription'),
    keywords: t('rentPage.metaKeywords'),
    path: '/',
    lang: language,
  });

  useEffect(() => {
    let cancelled = false;
    fetchAllTools()
      .then((data) => {
        if (cancelled) return;
        if (data.length > 0) setTools(data);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={jsonLd(buildLocalBusiness(language))} />
      {/* FAQ schema mirrors the visible FAQ below (Google requires the match) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={jsonLd({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqKeys.map((key) => ({
            '@type': 'Question',
            name: t(`rentPage.${key}q`),
            acceptedAnswer: { '@type': 'Answer', text: t(`rentPage.${key}a`) },
          })),
        })}
      />
      {visibleTools.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: visibleTools.map((tool, i) => {
              const name = getToolName(tool, language);
              const slug = toolLandingPages[tool.id]?.slug;
              return {
                '@type': 'ListItem',
                position: i + 1,
                url: absoluteUrl(lp(slug ? `/rent/${slug}` : '/')),
                name,
              };
            }),
          }) }}
        />
      )}
      <Header />

      <main className="pb-20 xl:pb-0">
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="bg-dewalt px-4 pt-8 pb-10 xl:pt-16 xl:pb-16 text-center" aria-labelledby="hero-heading">
          <div className="xl:max-w-[720px] xl:mx-auto">
            <h1 id="hero-heading" className="text-[28px] sm:text-[32px] xl:text-[46px] font-extrabold text-black leading-tight m-0 mb-3">
              {t('rentPage.heroTitle')}
            </h1>
            <p className="text-[16px] xl:text-[19px] text-black/80 m-0 mb-8 font-medium">
              {t('rentPage.heroSubtitle')}
            </p>
            <CallbackCTA t={t} onOpen={() => openCallback()} placement="hero" />
          </div>
        </section>

        {/* ── Benefits ──────────────────────────────────────────────────── */}
        <section className="px-4 py-8 max-w-[600px] xl:max-w-[1040px] mx-auto" aria-label="переваги">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4">
            {[
              { icon: '🚫💰', title: t('rentPage.benefit1'), desc: t('rentPage.benefit1Desc') },
              { icon: '🚗',   title: t('rentPage.benefit2'), desc: t('rentPage.benefit2Desc') },
              { icon: '🔧',   title: t('rentPage.benefit3'), desc: t('rentPage.benefit3Desc') },
              { icon: '⚡',   title: t('rentPage.benefit4'), desc: t('rentPage.benefit4Desc') },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="bg-gray-50 rounded-2xl p-4 xl:p-5 text-center">
                <div className="text-2xl mb-2" aria-hidden="true">{icon}</div>
                <div className="font-bold text-[14px] text-black mb-1">{title}</div>
                <div className="text-[12px] text-gray-500 leading-snug">{desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Tools grid ────────────────────────────────────────────────── */}
        <section className="px-4 pb-8 max-w-[600px] xl:max-w-[1040px] mx-auto" aria-labelledby="tools-heading">
          <h2 id="tools-heading" className="text-xl xl:text-2xl font-bold text-black mb-4 xl:mb-6 text-center">
            {t('rentPage.toolsTitle')}
          </h2>
          {loading ? (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4" aria-busy="true" aria-label={t('rentPage.toolsLoading')}>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-40 animate-pulse" />
              ))}
            </div>
          ) : visibleTools.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{t('rentPage.toolsEmpty')}</p>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4">
              {visibleTools.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  language={language}
                  t={t}
                  onSelect={setSelectedTool}
                  landingSlug={toolLandingPages[tool.id]?.slug}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── Intro text — SEO prose, visually subtle, always in DOM for Google */}
        <section className="px-4 pt-2 pb-6 max-w-[600px] xl:max-w-[1040px] mx-auto border-t border-gray-100" aria-labelledby="intro-heading">
          <h2 id="intro-heading" className="text-[13px] font-semibold text-gray-400 mb-1 text-center uppercase tracking-wide">
            {t('rentPage.introTitle')}
          </h2>
          <p className="text-[12px] text-gray-400 leading-relaxed text-center m-0">
            {t('rentPage.introText')}
          </p>
        </section>

        {/* ── FAQ — <details>/<summary> keeps answers in DOM for Google ─── */}
        <section className="px-4 pb-8 max-w-[600px] xl:max-w-[1040px] mx-auto" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-xl xl:text-2xl font-bold text-black mb-4 xl:mb-6 text-center">
            {t('rentPage.faqTitle')}
          </h2>
          <div className="flex flex-col gap-2">
            {faqKeys.map((key) => (
              <details key={key} className="group border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-4 py-3 font-semibold text-[14px] text-black cursor-pointer bg-white list-none [&::-webkit-details-marker]:hidden">
                  <span>{t(`rentPage.${key}q`)}</span>
                  <span className="ml-2 text-dewalt transition-transform duration-200 group-open:rotate-180" aria-hidden="true">▼</span>
                </summary>
                <div className="px-4 pb-3 text-[13px] text-gray-600 leading-relaxed bg-gray-50">
                  {t(`rentPage.${key}a`)}
                </div>
              </details>
            ))}
          </div>
        </section>

        <div className="px-4 pb-8 max-w-[600px] xl:max-w-[1040px] mx-auto">
          <ReviewPrompt placement="home" />
        </div>

        {/* ── Extra RU SEO prose — only on the Russian page (/ru) ─────── */}
        {language === 'ru' && (
          <section className="px-4 py-8 max-w-[600px] xl:max-w-[1040px] mx-auto border-t border-gray-100">
            <h2 className="text-lg font-bold text-black mb-3">
              Аренда инструмента в Харькове без залога
            </h2>
            <p className="text-[13px] text-gray-600 leading-relaxed">
              SmarTOOL — прокат профессионального электроинструмента в Харькове, ул. Сумская, 49.
              Аренда перфоратора, болгарки, шуруповёрта, отбойного молотка, штробореза, лобзика и шлифмашины
              без залога и с доставкой по городу. Инструмент DeWalt, Makita и Bosch проходит техобслуживание
              и всегда готов к работе. Звоните — подберём инструмент и доставим в удобное время.
            </p>
          </section>
        )}

        {selectedTool && (
          <ToolBottomSheet
            tool={selectedTool}
            language={language}
            t={t}
            onClose={() => setSelectedTool(null)}
            onCallbackOpen={(toolName) => {
              const recommended = (selectedTool?.recommendedToolIds || [])
                .map((id) => {
                  const found = tools.find((t) => t.id === id);
                  return found?.name?.[language] || found?.name?.uk || null;
                })
                .filter(Boolean);
              openCallback(toolName, recommended);
            }}
          />
        )}

        <CallbackModal isOpen={showCallback} onClose={() => setShowCallback(false)} toolName={callbackToolName} recommended={callbackRecommended} />

        {/* ── Bottom CTA ────────────────────────────────────────────────── */}
        <section className="bg-dewalt px-4 py-8 xl:py-14 text-center" aria-labelledby="cta-heading">
          <div className="xl:max-w-[720px] xl:mx-auto">
            <h2 id="cta-heading" className="text-xl xl:text-3xl font-bold text-black m-0 mb-2">
              {t('rentPage.ctaTitle')}
            </h2>
            <p className="text-black/70 text-sm xl:text-base m-0 mb-5">{t('rentPage.ctaSubtitle')}</p>
            <CallbackCTA t={t} onOpen={() => openCallback()} placement="footer" />
            <address className="not-italic mt-4 text-black/60 text-sm">
              {language === 'uk' ? 'Харків, вул. Сумська, 49' : 'Харьков, ул. Сумская, 49'}
            </address>
            <div className="mt-4 flex justify-center gap-4 text-sm">
              <Link to={lp('/about')} className="text-black/60 underline">
                {language === 'uk' ? 'Про нас' : 'О нас'}
              </Link>
              <Link to={lp('/how-it-works')} className="text-black/60 underline">
                {language === 'uk' ? 'Як це працює' : 'Как это работает'}
              </Link>
              <Link to={lp('/business')} className="text-black/60 underline">
                {language === 'uk' ? 'Для бізнесу' : 'Для бизнеса'}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <StickyCtaBar onCallback={() => openCallback()} />
    </div>
  );
}
